(() => {
const wbdText = (key, fallback) => window.variantStrings?.wbd?.[key] || fallback;
const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', {
  style: 'currency', currency: window.Shopify?.currency?.active || 'EUR'
}).format(Number(cents || 0) / 100);
const emit = (target, name, detail) => target.dispatchEvent(new CustomEvent(`wbd:${name}`, { bubbles: true, detail }));
const stores = new WeakMap();
const syncVariantImage = (image, variant) => {
  if (!image || !variant.featured_image) return;
  const url = new URL(variant.featured_image, location.href);
  url.searchParams.set('width', image.getAttribute('width'));
  image.src = url.href;
  image.alt = variant.image_alt;
  image.dataset.mediaId = variant.image_media_id;
};

class VariantState {
  constructor(root, variants, forms, product = null) {
    this.root = root;
    this.variants = variants;
    this.forms = forms;
    this.product = product;
    this.buttons = forms.flatMap((form) => [...form.querySelectorAll('[data-wbd-option], [data-wbd-card-option]')]);
    this.inputs = forms.map((form) => form.querySelector('[name="id"]'));
    this.submits = forms.map((form) => form.querySelector('[name="add"]'));
    this.current = variants.find((v) => String(v.id) === this.inputs[0].value) || variants[0];
    this.pending = false;
    product?.querySelector('.wbd-product__summary')?.classList.add('is-enhanced');
    forms.forEach((form) => {
      stores.set(form, this);
      form.addEventListener('wbd:product-loading', (event) => {
        this.pending = event.detail.loading;
        this.render();
      });
    });
    this.render();
  }

  select(id, source = 'option', updateURL = true) {
    if (this.pending) return;
    const variant = this.variants.find((v) => String(v.id) === String(id));
    if (!variant) return;
    const changed = this.current?.id !== variant.id;
    this.current = variant;
    this.render();
    this.forms.forEach((form) => form.closest('product-form')?.handleErrorMessage());
    if (this.product && changed) {
      this.product.querySelector('wbd-gallery')?.select(variant.featured_media_id, 'variant');
      if (updateURL) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState(history.state, '', url);
      }
    }
    if (changed) emit(this.root, 'variant_select', { variantId: variant.id, available: variant.available, source });
  }

  choose(button) {
    const options = this.current.options.slice();
    options[Number(button.dataset.position) - 1] = button.dataset.value;
    const position = Number(button.dataset.position) - 1;
    const matches = this.variants.filter((v) => v.options[position] === button.dataset.value);
    const variant = matches.find((v) => v.options.every((value, i) => value === options[i])) || matches.find((v) => v.available) || matches[0];
    if (variant) this.select(variant.id);
  }

  render() {
    const v = this.current;
    if (!v) return;
    this.inputs.forEach((input) => { input.value = String(v.id); });
    this.buttons.forEach((button) => {
      const position = Number(button.dataset.position) - 1;
      const matches = this.variants.filter((item) => item.options[position] === button.dataset.value);
      const candidate = matches.find((item) => item.options.every((value, i) => value === (i === position ? button.dataset.value : v.options[i]))) || matches.find((item) => item.available) || matches[0];
      const selected = v.options[position] === button.dataset.value;
      button.disabled = this.pending || !candidate;
      button.classList.toggle('is-active', selected);
      button.classList.toggle('is-disabled', !candidate);
      button.classList.toggle('is-sold-out', !!candidate && !candidate.available);
      button.dataset.stockLabel = window.variantStrings?.soldOut || 'Uitverkocht';
      button.setAttribute('aria-pressed', String(selected));
      button.setAttribute('aria-label', `${button.dataset.value}${candidate ? (candidate.available ? '' : ` — ${window.variantStrings?.soldOut || 'Uitverkocht'}`) : ` — ${window.variantStrings?.unavailable || 'Niet beschikbaar'}`}`);
    });
    this.submits.forEach((button) => {
      if (!button) return;
      button.disabled = this.pending || !v.available;
      const label = button.querySelector('span:not(.sold-out-message):not(.loading__spinner)');
      if (label) label.textContent = v.available ? button.dataset.addText || window.variantStrings?.addToCart || 'In winkelwagen' : button.dataset.soldOutText || window.variantStrings?.soldOut || 'Uitverkocht';
    });
    const scopes = [...new Set([this.root, ...this.forms.map((form) => form.closest('[data-wbd-variant-modal]')).filter(Boolean)])];
    scopes.forEach((scope) => {
      scope.querySelectorAll('[data-wbd-price], [data-wbd-card-modal-price]').forEach((el) => { el.textContent = money(v.price); });
      const sale = Number(v.compare_at_price) > v.price;
      scope.querySelectorAll('[data-wbd-compare], [data-wbd-discount]').forEach((el) => {
        el.classList.toggle('hidden', !sale);
        if (el.hasAttribute('data-wbd-compare')) el.textContent = money(v.compare_at_price);
      });
      const image = scope.querySelector('[data-wbd-card-modal-image]');
      syncVariantImage(image, v);
    });
    const stock = this.root.querySelector('[data-wbd-stock]');
    if (stock) {
      const low = v.available && v.inventory_management && v.inventory_quantity > 0 && v.inventory_quantity <= 10;
      stock.classList.remove('wbd-stock--green', 'wbd-stock--yellow', 'wbd-stock--red');
      stock.classList.add(`wbd-stock--${!v.available ? 'red' : low ? 'yellow' : 'green'}`);
      stock.querySelector('[data-wbd-stock-main]').textContent = !v.available ? stock.dataset.soldOutText : low ? stock.dataset.lowStockText.replace('[quantity]', v.inventory_quantity) : stock.dataset.inStockText;
      const delivery = stock.querySelector('[data-wbd-stock-delivery]');
      if (delivery) delivery.hidden = !v.available;
    }
    if (this.product) {
      const sticky = document.querySelector(`[data-wbd-sticky-atc][data-wbd-product-owner="${CSS.escape(this.product.dataset.wbdProduct)}"]`);
      if (sticky) {
        sticky.querySelector('[data-wbd-sticky-price]').textContent = money(v.price);
        const button = sticky.querySelector('[data-wbd-sticky-button]');
        const anyAvailable = this.variants.some((variant) => variant.available);
        button.disabled = this.pending || !anyAvailable;
        button.querySelector('span').textContent = this.pending ? wbdText('loading', 'Bezig…') : !anyAvailable ? window.variantStrings?.soldOut || 'Uitverkocht' : v.available ? button.dataset.addText : button.dataset.soldOutText;
        const image = sticky.querySelector('[data-wbd-sticky-image]');
        syncVariantImage(image, v);
      }
    }
  }
}

const getStore = (form) => {
  if (!form) return null;
  if (stores.has(form)) return stores.get(form);
  const owner = form.closest('[data-wbd-product], [data-wbd-product-owner]');
  const product = owner?.hasAttribute('data-wbd-product') ? owner : owner ? document.querySelector(`[data-wbd-product="${CSS.escape(owner.dataset.wbdProductOwner)}"]`) : null;
  const root = product || form.closest('[data-wbd-variant-modal]');
  const script = root?.querySelector(product ? '[data-wbd-variants]' : '[data-wbd-card-variants]');
  if (!script) return null;
  const forms = product ? [...product.querySelectorAll('[data-wbd-product-form]'), ...document.querySelectorAll(`[data-wbd-product-owner="${CSS.escape(product.dataset.wbdProduct)}"] [data-wbd-card-product-form]`)] : [form];
  return new VariantState(root, JSON.parse(script.textContent), forms, product);
};
const wbdGetVariantModalData = (modal) => {
  const form = modal?.querySelector('[data-wbd-card-product-form]');
  const state = getStore(form);
  return state ? { form, input: form.querySelector('[name="id"]'), variants: state.variants } : null;
};
const wbdSetVariantModalVariant = (modal, id) => getStore(modal?.querySelector('form'))?.select(id, 'collection', false);

const syncModalLock = () => document.documentElement.classList.toggle('wbd-modal-open', !!document.querySelector('dialog[open]'));
const closeModal = (modal) => { if (modal?.open) modal.close(); };
const openModal = (modal, opener) => {
  if (!modal || modal.open) return;
  getStore(modal.querySelector('form'))?.render();
  modal.wbdOpener = opener;
  if (document.querySelector('cart-drawer.active')) removeTrapFocus();
  modal.showModal();
  syncModalLock();
  (modal.querySelector('.is-active:not(:disabled)') || modal.querySelector('button'))?.focus();
};
document.addEventListener('close', (event) => {
  const modal = event.target;
  if (!(modal instanceof HTMLDialogElement)) return;
  syncModalLock();
  if (modal.wbdOpener?.isConnected) modal.wbdOpener.focus();
  const drawer = document.querySelector('cart-drawer.active');
  if (drawer) trapFocus(drawer.querySelector('.drawer__inner'), modal.wbdOpener?.isConnected ? modal.wbdOpener : drawer.querySelector('.drawer__close'));
}, true);
document.addEventListener('wbd:product-added', (event) => {
  const modal = event.target.closest('[data-wbd-variant-modal]');
  if (!modal) return;
  closeModal(modal);
  document.querySelector('cart-drawer')?.setActiveElement(modal.wbdOpener);
});
document.addEventListener('click', (event) => {
  const sizeGuide = event.target.closest('[data-wbd-size-open]');
  if (sizeGuide) {
    openModal(document.getElementById(sizeGuide.dataset.wbdSizeOpen), sizeGuide);
    emit(sizeGuide, 'size_guide_open', { dialogId: sizeGuide.dataset.wbdSizeOpen });
    return;
  }
  const sizeCloser = event.target.closest('[data-wbd-size-close]');
  if (sizeCloser) { closeModal(sizeCloser.closest('dialog')); return; }
  if (event.target.matches('dialog[data-wbd-size-dialog]')) {
    const rect = event.target.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeModal(event.target);
    return;
  }
  const opener = event.target.closest('[data-wbd-variant-modal-open]');
  if (opener) {
    event.preventDefault();
    const modal = opener.hasAttribute('data-wbd-sticky-button') ? document.querySelector('[data-wbd-sticky-variant-modal]') : opener.closest('.wbd-product-card, .wbd-cart-upsell-card')?.querySelector('[data-wbd-variant-modal]');
    openModal(modal, opener);
    return;
  }
  const closer = event.target.closest('[data-wbd-variant-modal-close]');
  if (closer) { closeModal(closer.closest('dialog')); return; }
  const option = event.target.closest('[data-wbd-option], [data-wbd-card-option]');
  if (option) { getStore(option.closest('form'))?.choose(option); return; }
  const tab = event.target.closest('[data-wbd-tab]');
  if (tab && desktopTabs.matches) {
    const tabs = tab.closest('[data-wbd-tabs]');
    tabs.querySelectorAll('[data-wbd-tab]').forEach((item) => {
      const active = item === tab;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-selected', String(active));
      item.tabIndex = active ? 0 : -1;
    });
    tabs.querySelectorAll('[data-wbd-panel]').forEach((panel) => { panel.classList.toggle('is-active', panel.dataset.wbdPanel === tab.dataset.wbdTab); });
  }
});
document.addEventListener('change', (event) => {
  if (event.target.matches('[data-wbd-variant-select]')) getStore(event.target.closest('form'))?.select(event.target.value, 'select');
});
window.addEventListener('popstate', () => document.querySelectorAll('[data-wbd-product-form]').forEach((form) => {
  const state = getStore(form);
  state?.select(new URL(location.href).searchParams.get('variant') || state.variants[0].id, 'history', false);
}));

customElements.define('wbd-gallery', class extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.viewer = this.querySelector('[id^="Slider-"]');
    this.slides = [...this.querySelectorAll('.wbd-gallery__slide')];
    this.thumbs = [...this.querySelectorAll('[data-wbd-thumb]')];
    this.status = this.querySelector('[data-wbd-gallery-status]');
    this.slider = this.querySelector('slider-component');
    this.lightbox = this.querySelector('dialog');
    this.slider.addEventListener('slideChanged', (event) => {
      if (event.detail.currentElement) this.update(event.detail.currentElement, 'navigation');
    }, { signal });
    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-wbd-thumb]');
      if (thumb) this.select(thumb.dataset.wbdThumb, 'thumbnail');
      const zoom = event.target.closest('[data-wbd-zoom]');
      if (zoom) {
        const image = zoom.querySelector('img').cloneNode();
        image.sizes = '95vw';
        this.querySelector('[data-wbd-zoom-image]').replaceChildren(image);
        this.lightbox.showModal();
        syncModalLock();
      }
    }, { signal });
    this.viewer.addEventListener('keydown', (event) => {
      if (event.target.closest('deferred-media, product-model')) return;
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === this.currentId);
      const next = { ArrowLeft: index - 1, ArrowRight: index + 1, Home: 0, End: this.slides.length - 1 }[event.key];
      if (next === undefined) return;
      event.preventDefault();
      this.select(this.slides[Math.max(0, Math.min(next, this.slides.length - 1))]?.dataset.mediaId, 'keyboard');
    }, { signal });
    this.frame = requestAnimationFrame(() => this.select(this.dataset.currentMedia || this.slides[0]?.dataset.mediaId, 'initial'));
  }
  disconnectedCallback() { this.controller?.abort(); this.controller = null; cancelAnimationFrame(this.frame); }
  select(id, source) {
    const slide = this.slides.find((item) => item.dataset.mediaId === String(id));
    if (!slide) return;
    this.update(slide, source);
    this.viewer.scrollTo({ left: slide.offsetLeft - this.slides[0].offsetLeft, behavior: 'instant' });
    this.slider.update();
  }
  update(slide, source) {
    const changed = this.currentId !== slide.dataset.mediaId;
    this.currentId = slide.dataset.mediaId;
    const index = this.slides.indexOf(slide);
    this.slides.forEach((item) => { item.inert = item !== slide; item.setAttribute('aria-hidden', String(item !== slide)); });
    this.thumbs.forEach((thumb) => {
      const active = thumb.dataset.wbdThumb === this.currentId;
      thumb.classList.toggle('is-active', active);
      if (active) {
        thumb.setAttribute('aria-current', 'true');
        const track = thumb.parentElement;
        track.scrollTo({ left: thumb.offsetLeft - track.offsetLeft - (track.clientWidth - thumb.clientWidth) / 2 });
      } else thumb.removeAttribute('aria-current');
    });
    this.status.textContent = wbdText('media_status', 'Media [index] van [total]').replace('[index]', index + 1).replace('[total]', this.slides.length);
    if (changed) {
      window.pauseAllMedia();
      if (source !== 'initial') emit(this, 'gallery_change', { mediaId: this.currentId, position: index + 1, source });
    }
  }
});

const desktopTabs = window.matchMedia('(min-width: 990px)');
const updateTabSemantics = (tabs) => {
  const desktop = desktopTabs.matches;
  const nav = tabs.querySelector('.wbd-tabs__nav');
  if (desktop) nav.setAttribute('role', 'tablist');
  else nav.removeAttribute('role');
  tabs.querySelectorAll('[data-wbd-tab]').forEach((tab) => {
    const panel = tabs.querySelector(`[data-wbd-panel="${tab.dataset.wbdTab}"]`);
    tab.id = `${panel.id}-tab`;
    if (desktop) {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', String(tab.classList.contains('is-active')));
      tab.setAttribute('aria-controls', panel.id);
      tab.tabIndex = tab.classList.contains('is-active') ? 0 : -1;
    } else {
      ['role', 'aria-selected', 'aria-controls', 'tabindex'].forEach((name) => tab.removeAttribute(name));
    }
    panel.setAttribute('role', desktop ? 'tabpanel' : 'region');
    panel.setAttribute('aria-labelledby', desktop ? tab.id : panel.querySelector('.wbd-mobile-panel-title').id);
  });
};
desktopTabs.addEventListener('change', () => document.querySelectorAll('[data-wbd-tabs]').forEach(updateTabSemantics));
const stickyObservers = new WeakMap();
const initializeProducts = (root = document) => {
  root.querySelectorAll('[data-wbd-product-form]').forEach(getStore);
  root.querySelectorAll('[data-wbd-sticky-atc]').forEach((sticky) => {
    if (stickyObservers.has(sticky)) return;
    const product = document.querySelector(`[data-wbd-product="${CSS.escape(sticky.dataset.wbdProductOwner)}"]`);
    const button = product?.querySelector('[data-wbd-add]');
    if (!button) return;
    const observer = new IntersectionObserver(([entry]) => {
      const show = entry.intersectionRatio < 0.5;
      sticky.classList.toggle('is-visible', show);
      sticky.inert = !show;
      document.documentElement.classList.toggle('wbd-sticky-atc-visible', show);
    }, { threshold: [0, 0.5, 1] });
    observer.observe(button);
    stickyObservers.set(sticky, observer);
  });
  root.querySelectorAll('[data-wbd-tabs]').forEach((tabs) => {
    if (tabs.dataset.enhanced) return;
    tabs.dataset.enhanced = 'true';
    updateTabSemantics(tabs);
    tabs.addEventListener('keydown', (event) => {
        const tab = event.target.closest('[data-wbd-tab]');
        if (!desktopTabs.matches || !tab) return;
        const buttons = [...tabs.querySelectorAll('[data-wbd-tab]')];
        const current = buttons.indexOf(tab);
        const next = { ArrowRight: (current + 1) % buttons.length, ArrowLeft: (current + buttons.length - 1) % buttons.length, Home: 0, End: buttons.length - 1 }[event.key];
        if (next !== undefined) { event.preventDefault(); buttons[next].click(); buttons[next].focus(); }
    });
  });
};
document.addEventListener('DOMContentLoaded', () => initializeProducts(), { once: true });
document.addEventListener('shopify:section:load', (event) => initializeProducts(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-wbd-sticky-atc]').forEach((sticky) => {
    stickyObservers.get(sticky)?.disconnect();
    stickyObservers.delete(sticky);
  });
});

const wbdStorage = {
  getItem(key) { try { return localStorage.getItem(key); } catch { return null; } },
  setItem(key, value) { try { localStorage.setItem(key, value); } catch {} },
  removeItem(key) { try { localStorage.removeItem(key); } catch {} }
};
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-wbd-color-nav]');
  if (!button) return;

  const slider = button.closest('[data-wbd-color-siblings]');
  const track = slider && slider.querySelector('[data-wbd-color-track]');
  if (!track) return;

  const direction = button.dataset.wbdColorNav === 'prev' ? -1 : 1;

  track.scrollBy({
    left: direction * Math.max(220, track.clientWidth * 0.75),
    behavior: 'smooth'
  });
});


/* =========================================================
   WBD COLLECTIE MAATFILTER
   ========================================================= */

const wbdGetCollectionCardVariants = (card) => {
  return wbdGetVariantModalData(card.querySelector('[data-wbd-variant-modal]'))?.variants || [];
};

const wbdUpdateCollectionCardLinks = (card, variantId = '') => {
  card
    .querySelectorAll('.wbd-product-card__image, h3 a')
    .forEach((link) => {
      if (!link.dataset.wbdBaseHref) {
        link.dataset.wbdBaseHref = link.getAttribute('href') || '';
      }

      const baseHref = link.dataset.wbdBaseHref;

      if (!variantId) {
        link.setAttribute('href', baseHref);
        return;
      }

      const url = new URL(baseHref, window.location.origin);
      url.searchParams.set('variant', variantId);

      link.setAttribute(
        'href',
        `${url.pathname}${url.search}${url.hash}`
      );
    });
};

const wbdUpdateCollectionProductCount = (grid) => {
  if (!grid) return;

  const items = [
    ...grid.querySelectorAll('.grid__item')
  ];

  const visibleCount = items.filter(
    (item) => !item.hidden
  ).length;

  const text = `${visibleCount} ${
    visibleCount === 1 ? 'product' : 'producten'
  }`;

  document
    .querySelectorAll(
      '#ProductCount, #ProductCountDesktop, .mobile-facets__count'
    )
    .forEach((count) => {
      count.textContent = text;
    });

  const container =
    document.querySelector('#ProductGridContainer');

  if (!container) return;

  let emptyState =
    container.querySelector('[data-wbd-empty-state]');

  if (!emptyState) {
    emptyState = document.createElement('div');

    emptyState.className = 'wbd-collection-empty';
    emptyState.setAttribute(
      'data-wbd-empty-state',
      ''
    );

    emptyState.innerHTML = `
      <div class="wbd-collection-empty__inner">
        <h2>Helaas, geen producten gevonden</h2>
        <p>
          Pas je keuze of filters aan om andere beschikbare producten te bekijken.
        </p>
      </div>
    `;

    grid.insertAdjacentElement(
      'afterend',
      emptyState
    );
  }

  emptyState.hidden = visibleCount !== 0;
};

const wbdApplyCollectionSizeFilter = (sizeValue = '') => {
  const grid = document.querySelector('#product-grid');
  if (!grid) return;

  grid.querySelectorAll('.wbd-product-card').forEach((card) => {
    const gridItem = card.closest('.grid__item');
    const modal = card.querySelector('[data-wbd-variant-modal]');
    const variants = wbdGetCollectionCardVariants(card);

    if (!gridItem || !modal || variants.length === 0) return;

    const modalData = wbdGetVariantModalData(modal);

    if (
      modalData &&
      modalData.input &&
      !card.dataset.wbdInitialVariantId
    ) {
      card.dataset.wbdInitialVariantId = modalData.input.value;
    }

    /* Geen maat gekozen: alles terugzetten */
    if (!sizeValue) {
      gridItem.hidden = false;

      wbdUpdateCollectionCardLinks(card);

      if (card.dataset.wbdInitialVariantId) {
        wbdSetVariantModalVariant(
          modal,
          card.dataset.wbdInitialVariantId
        );
      }

      return;
    }

    /* Zoek exact de variant die bij deze maat hoort */
    const matchingVariant = variants.find((variant) => {
      return (
        variant.available && Array.isArray(variant.options) &&
        variant.options.some(
          (option) => String(option).trim() === sizeValue
        )
      );
    });

    /* Alleen tonen als die specifieke maat bestaat én op voorraad is */
    const shouldShow =
      matchingVariant && matchingVariant.available;

    gridItem.hidden = !shouldShow;

    if (!shouldShow) return;

    /* Juiste maat alvast selecteren */
    wbdSetVariantModalVariant(
      modal,
      matchingVariant.id
    );

    /* Productlink opent direct de juiste variant */
    wbdUpdateCollectionCardLinks(
      card,
      matchingVariant.id
    );
  });
  wbdUpdateCollectionProductCount(grid);
};

/* =========================================================
   WBD GEKOZEN MAAT ONTHOUDEN
   Geschikt voor modelkeuze + algemene maatfilters
   ========================================================= */

const wbdCollectionSizeStorageKey = () => {
  return `wbd-collection-size:${window.location.pathname}`;
};

const wbdGetSizeChoiceKey = (choice) => {
  return (
    choice.dataset.wbdSizeKey ??
    choice.dataset.wbdSize ??
    ''
  );
};

const wbdGetSizeChoiceValue = (choice) => {
  return (
    choice.dataset.wbdSizeValue ??
    choice.dataset.wbdSize ??
    ''
  );
};

const wbdGetSizeChoiceLabel = (choice) => {
  return (
    choice.dataset.wbdSizeLabel ||
    choice.textContent.trim()
  );
};

const wbdGetAllSizeChoices = () => {
  return [
    ...document.querySelectorAll(
      '.wbd-model-size-filter__button, [data-wbd-size-choice]'
    )
  ];
};

const wbdSyncCollectionSizeUi = (activeKey = '') => {
  const choices = wbdGetAllSizeChoices();

  const activeChoice = choices.find(
    (choice) => wbdGetSizeChoiceKey(choice) === activeKey
  );

  choices.forEach((choice) => {
    const isActive =
      wbdGetSizeChoiceKey(choice) === activeKey;

    choice.classList.toggle('is-active', isActive);
    choice.setAttribute(
      'aria-pressed',
      isActive ? 'true' : 'false'
    );
  });

  /* Voor de normale filterbalk die we hierna toevoegen */
  document
    .querySelectorAll('[data-wbd-size-filter-current]')
    .forEach((label) => {
      if (activeKey && activeChoice) {
        label.textContent =
          wbdGetSizeChoiceLabel(activeChoice);
      } else {
        label.textContent =
          label.dataset.defaultLabel || 'Watch-maat';
      }
    });
};

const wbdRestoreCollectionSizeFilter = () => {
  const filters = [
    ...document.querySelectorAll('[data-wbd-size-filter]')
  ];

  if (!filters.length) return;

  /* Collecties zoals Ultra / Samsung modelcollecties */
  const fixedFilter = filters.find(
    (filter) =>
      filter.dataset.wbdFilterMode === 'fixed'
  );

  if (fixedFilter) {
    wbdApplyCollectionSizeFilter(
      fixedFilter.dataset.wbdFixedSize || ''
    );
    return;
  }

  const choices = wbdGetAllSizeChoices();
  if (!choices.length) return;

  const savedKey =
    wbdStorage.getItem(
      wbdCollectionSizeStorageKey()
    ) || '';

  /*
    Eerst zoeken op de echte klantkeuze.
    Fallback houdt je huidige modelselector compatibel.
  */
  let activeChoice = choices.find(
    (choice) =>
      wbdGetSizeChoiceKey(choice) === savedKey
  );

  if (!activeChoice && savedKey) {
    activeChoice = choices.find(
      (choice) =>
        wbdGetSizeChoiceValue(choice) === savedKey
    );
  }

  if (!activeChoice) {
    activeChoice = choices.find(
      (choice) =>
        wbdGetSizeChoiceKey(choice) === ''
    );
  }

  const activeKey = activeChoice
    ? wbdGetSizeChoiceKey(activeChoice)
    : '';

  const sizeValue = activeChoice
    ? wbdGetSizeChoiceValue(activeChoice)
    : '';

  wbdSyncCollectionSizeUi(activeKey);
  wbdApplyCollectionSizeFilter(sizeValue);
};

document.addEventListener('click', (event) => {
  const choice = event.target.closest(
    '.wbd-model-size-filter__button, [data-wbd-size-choice]'
  );

  if (!choice) return;

  const filter = choice.closest('[data-wbd-size-filter]');
  if (!filter) return;

  const sizeKey = wbdGetSizeChoiceKey(choice);
  const sizeValue = wbdGetSizeChoiceValue(choice);

  if (sizeKey) {
    wbdStorage.setItem(
      wbdCollectionSizeStorageKey(),
      sizeKey
    );
  } else {
    wbdStorage.removeItem(
      wbdCollectionSizeStorageKey()
    );
  }

  wbdSyncCollectionSizeUi(sizeKey);
  wbdApplyCollectionSizeFilter(sizeValue);
});


document.addEventListener('DOMContentLoaded', wbdRestoreCollectionSizeFilter, { once: true });
document.addEventListener('wbd:collection-rendered', wbdRestoreCollectionSizeFilter);

})();
